// "Sugerir pelo histórico" (espec. 10.1): um botão por linha de grupo pede ao
// servidor um perfil de desembolso baseado no histórico de pagamentos do alvo.
// A resposta é sempre uma PROPOSTA — fica na tela com o resumo da evidência até o
// cliente clicar "Usar no editor" (o que preenche o editor via `onSuggestion`) ou
// "Descartar" (some sem efeito nenhum). Nada é gravado por aqui: quem grava é o
// "Salvar" do próprio editor, como sempre.
import { useEffect, useRef, useState } from "react";

import { describeProfile } from "../profile";
import { suggestDisbursementProfile } from "./disbursementAiApi";

const GENERIC_ERROR_MESSAGE = "A IA está temporariamente indisponível.";

const CONFIDENCE_LABEL = Object.freeze({
  high: "alta",
  medium: "média",
  low: "baixa",
});

// Mesmo vocabulário do `disbursementOutput.js` do back-end (Task 9): os 7 códigos
// de ressalva que a rota pode devolver, cada um com a frase que o cliente lê.
const CAVEAT_LABEL = Object.freeze({
  share_approximation: "Fatia por insumo é aproximada",
  sem_historico: "Sem histórico de pagamentos",
  single_work: "Histórico de uma obra só",
  sparse_months: "Pagamentos muito espalhados",
  truncated: "Lista de insumos truncada",
  low_volume: "Poucos pagamentos observados",
  market_default: "Padrão de mercado, não do histórico",
});

const plural = (count, singular, many) =>
  `${count} ${count === 1 ? singular : many}`;

const safeProfileSummary = (profile) => {
  try {
    return describeProfile(profile);
  } catch {
    // Uma proposta que o validador recusaria não pode derrubar a tela — o
    // cliente ainda vê a evidência e decide se descarta.
    return null;
  }
};

const SuggestFromHistoryButton = ({
  workId,
  targetKey,
  targetLabel,
  onSuggestion,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const requestVersion = useRef(0);
  const contextRef = useRef(JSON.stringify([workId, targetKey]));
  useEffect(() => {
    const context = JSON.stringify([workId, targetKey]);
    if (context !== contextRef.current) {
      contextRef.current = context;
      requestVersion.current += 1;
      setLoading(false);
      setResult(null);
      setError(null);
    }
    return () => {
      requestVersion.current += 1;
    };
  }, [workId, targetKey]);

  const ask = () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    requestVersion.current += 1;
    const version = requestVersion.current;
    suggestDisbursementProfile(workId, { targetKey })
      .then((response) => {
        if (version === requestVersion.current) setResult(response);
      })
      .catch((requestError) => {
        if (version !== requestVersion.current) return;
        setError(requestError?.message ?? GENERIC_ERROR_MESSAGE);
      })
      .finally(() => {
        if (version === requestVersion.current) setLoading(false);
      });
  };

  const useInEditor = () => {
    if (!result || !safeProfileSummary(result.suggestion?.profile)) return;
    onSuggestion?.({
      target: { key: targetKey, label: targetLabel },
      profile: result.suggestion?.profile ?? [],
      evidence: result.evidence ?? null,
      confidence: result.confidence ?? "low",
    });
    setResult(null);
  };

  const discard = () => setResult(null);

  const totals = result?.evidence?.totals ?? null;
  const processing = result?.status === "processing";
  const profileSummary = result
    ? safeProfileSummary(result.suggestion?.profile)
    : null;

  return (
    <div className="disbursement-suggest-from-history">
      <button
        type="button"
        className="button button-secondary small"
        onClick={ask}
        disabled={loading}
      >
        Sugerir pelo histórico
      </button>

      {error ? (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      ) : null}

      {processing ? (
        <p role="status">
          A sugestão ainda está sendo preparada. Clique em Sugerir pelo
          histórico para consultar novamente.
        </p>
      ) : null}
      {result && !processing ? (
        <div
          className="disbursement-suggest-from-history__result"
          role="group"
          aria-label="Proposta pelo histórico"
        >
          <p>{result.suggestion?.rationale}</p>
          {profileSummary ? <p>{profileSummary}</p> : null}
          {totals ? (
            <p>
              {`${plural(totals.resourceCount, "insumo", "insumos")} · ${plural(
                totals.paymentCount,
                "pagamento",
                "pagamentos",
              )} · ${plural(totals.worksCount, "obra", "obras")}`}
            </p>
          ) : null}
          <p>{`Confiança: ${
            CONFIDENCE_LABEL[result.confidence] ?? "baixa"
          }`}</p>
          {(result.suggestion?.caveats ?? []).length > 0 ? (
            <ul>
              {result.suggestion.caveats.map((caveat) => (
                <li key={caveat}>
                  {CAVEAT_LABEL[caveat] ?? "Ressalva registrada"}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="disbursement-suggest-from-history__actions">
            <button
              type="button"
              className="button button-accent small"
              onClick={useInEditor}
              disabled={!profileSummary}
            >
              Usar no editor
            </button>
            <button
              type="button"
              className="button button-secondary small"
              onClick={discard}
            >
              Descartar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SuggestFromHistoryButton;
